import { FC } from 'react'

export const SkeletonLine: FC<{ className?: string }> = ({
  className = ''
}) => <div className={`animate-pulse bg-gray-200 rounded ${className}`} />

export const AnalysisLoadingSkeleton: FC = () => (
  <div className='bg-white rounded-lg shadow-sm p-6 mb-4'>
    <SkeletonLine className='h-6 w-48 mb-4' />
    <div className='space-y-3'>
      <SkeletonLine className='h-4 w-full' />
      <SkeletonLine className='h-4 w-3/4' />
      <SkeletonLine className='h-4 w-5/6' />
    </div>
  </div>
)

export const VideoLoadingSkeleton: FC = () => (
  <div className='bg-white rounded-lg shadow-sm p-4 mb-6'>
    <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
      {/* Video skeleton */}
      <div className='animate-pulse'>
        <div className='aspect-video md:aspect-square bg-gray-300 rounded-lg'></div>
      </div>
      {/* Session Summary skeleton */}
      <div className='animate-pulse space-y-3'>
        <div className='h-6 bg-gray-200 rounded w-1/2 mb-4'></div>
        <div className='grid grid-cols-2 gap-2'>
          <div className='h-4 bg-gray-200 rounded'></div>
          <div className='h-4 bg-gray-200 rounded'></div>
          <div className='h-4 bg-gray-200 rounded'></div>
          <div className='h-4 bg-gray-200 rounded'></div>
        </div>
      </div>
    </div>
  </div>
)

export const SessionSummaryLoadingSkeleton: FC = () => (
  <div className='bg-white rounded-lg shadow-sm p-6 mb-6'>
    <div className='flex justify-between'>
      <div className='space-y-3 flex-1'>
        <div className='flex items-center gap-3'>
          <SkeletonLine className='h-5 w-5 rounded-full' />
          <SkeletonLine className='h-4 w-32' />
        </div>
        <div className='flex items-center gap-3'>
          <SkeletonLine className='h-5 w-5 rounded-full' />
          <SkeletonLine className='h-4 w-40' />
        </div>
        <div className='flex items-center gap-3'>
          <SkeletonLine className='h-5 w-5 rounded-full' />
          <SkeletonLine className='h-4 w-28' />
        </div>
        <div className='flex items-center gap-3'>
          <SkeletonLine className='h-5 w-5 rounded-full' />
          <SkeletonLine className='h-4 w-36' />
        </div>
      </div>
      <SkeletonLine className='h-28 w-28 rounded-lg ml-4' />
    </div>
  </div>
)

export const ProcessingIndicator: FC<{ text?: string }> = ({
  text = 'Processing'
}) => {
  return (
    <div className='flex items-center gap-2 text-gray-600'>
      <span>{text}</span>
      <span className='inline-flex'>
        <span className='animate-pulse'>.</span>
        <span className='animate-pulse' style={{ animationDelay: '200ms' }}>
          .
        </span>
        <span className='animate-pulse' style={{ animationDelay: '400ms' }}>
          .
        </span>
      </span>
    </div>
  )
}

export const Spinner: FC = () => (
  <div className='animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500'></div>
)

export const TranscriptLoadingSkeleton: FC = () => (
  <div className='bg-white rounded-lg shadow-sm p-6'>
    <div className='flex items-center gap-2 mb-4'>
      <SkeletonLine className='h-5 w-5 rounded' />
      <SkeletonLine className='h-5 w-32' />
    </div>
    <div className='bg-gray-900 rounded-lg p-4'>
      <SkeletonLine className='h-3 w-40 mb-3 bg-gray-700' />
      <div className='space-y-3'>
        <div className='flex gap-2'>
          <SkeletonLine className='h-4 w-20 bg-gray-700' />
          <SkeletonLine className='h-4 flex-1 bg-gray-700' />
        </div>
        <div className='flex gap-2'>
          <SkeletonLine className='h-4 w-20 bg-gray-700' />
          <SkeletonLine className='h-4 w-3/4 bg-gray-700' />
        </div>
        <div className='flex gap-2'>
          <SkeletonLine className='h-4 w-20 bg-gray-700' />
          <SkeletonLine className='h-4 flex-1 bg-gray-700' />
        </div>
      </div>
    </div>
  </div>
)

export const PageLoadingSkeleton: FC = () => (
  <div className='max-w-4xl mx-auto px-4 py-8'>
    {/* Header */}
    <div className='mb-8 flex justify-between items-start'>
      <div>
        <SkeletonLine className='h-8 w-48 mb-2' />
        <SkeletonLine className='h-4 w-64' />
      </div>
      <SkeletonLine className='h-9 w-24 rounded-lg' />
    </div>

    {/* Video and Session Summary */}
    <VideoLoadingSkeleton />

    {/* AI Analysis */}
    <AnalysisLoadingSkeleton />

    {/* Additional AI cards */}
    <div className='space-y-4 mb-6'>
      <AnalysisLoadingSkeleton />
      <AnalysisLoadingSkeleton />
    </div>

    {/* Transcript */}
    <TranscriptLoadingSkeleton />
  </div>
)
